import {
    isCategoricalGroupByOption,
    isDateGroupByGrain,
    isNumericRangeGroupByOption,
    isRecord,
    isStrictlyIncreasing,
    toOptionalString,
    type GroupByConfiguration,
} from "./GroupByTypes";
import type { NumericRangeLimits } from "./groupByDraft";

/** Looks up what a range dimension's own filter can express, normally the group-by catalog. */
export type NumericRangeLimitsLookup = (
    option: string,
) => NumericRangeLimits | undefined;

/**
 * The grouping-related slice of vizOptions, typed as `unknown` because this module's job is to
 * decide whether what was persisted is usable. Structural rather than importing
 * `SignalsParsedVizOptions` so anything holding these fields -- a chart request, a persisted
 * record -- can be normalized the same way.
 */
export type GroupByConfigurationSource = {
    groupBy?: unknown;
    groupByOption?: unknown;
    groupByIds?: unknown;
    groupByLlmClassifierId?: unknown;
    groupByExtendedMetadataKey?: unknown;
};

const normalizeStringArray = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
        return undefined;
    }

    return value.map((item) => item.trim()).filter((item) => item.length > 0);
};

const normalizeCutPoints = (
    value: unknown,
    cutPointCeiling: number | null | undefined,
): number[] | undefined => {
    if (
        !Array.isArray(value) ||
        value.length === 0 ||
        value.some((point) => typeof point !== "number" || !Number.isFinite(point))
    ) {
        return undefined;
    }

    const cutPoints = value as number[];

    // A boundary at or above the dimension's ceiling describes a bucket the chart's own cap has
    // already emptied. `SignalsGroupByResolver` rejects one, and rejecting a request is what this
    // reader exists to avoid: a chart carrying it renders ungrouped instead of erroring.
    if (cutPointCeiling != null && cutPoints.some((point) => point >= cutPointCeiling)) {
        return undefined;
    }

    // Zero leads the sequence for the same reason it does in `validateGroupByDraft`: the first
    // bucket runs from zero, so a boundary at or below it opens a bucket nothing can fall into.
    return isStrictlyIncreasing([0, ...cutPoints]) ? [...cutPoints] : undefined;
};

/**
 * The categorical branch shared by canonical and legacy configuration. A value id list alone is not
 * enough for the two options that need extra context: Model Results without a classifier, or
 * Extended Metadata without a key, cannot be queried, so both normalize to `undefined` the same way
 * an empty value list does. Without this, a configuration missing one of those fields would
 * normalize as "runnable" here while `validateCategoricalDraft` already refuses to let the drawer
 * save it -- the two readers would disagree about what counts as complete.
 */
const normalizeCategoricalConfiguration = (
    option: unknown,
    rawValueIds: unknown,
    rawLlmClassifierId: unknown,
    rawExtendedMetadataKey: unknown,
    rawQuestionId?: unknown,
    rawLicensedModuleId?: unknown,
): GroupByConfiguration | undefined => {
    if (!isCategoricalGroupByOption(option)) return undefined;

    const valueIds = normalizeStringArray(rawValueIds);
    if (!valueIds?.length) return undefined;

    const llmClassifierId = toOptionalString(rawLlmClassifierId);
    if (option === "ModelResults" && !llmClassifierId) return undefined;

    const extendedMetadataKey = toOptionalString(rawExtendedMetadataKey);
    if (option === "ExtendedMetadata" && !extendedMetadataKey) return undefined;

    // Question Result needs both. Tag ids alone do not say which question they answer, and the
    // module is what the dashboard's permission pass keys on to hide charts grouped by a module the
    // viewer cannot see. A configuration missing either is not one this reader will hand back.
    const questionId = toOptionalString(rawQuestionId);
    const licensedModuleId = toOptionalString(rawLicensedModuleId);
    if (option === "QuestionResult" && (!licensedModuleId || !questionId)) {
        return undefined;
    }

    return {
        kind: "categorical",
        option,
        valueIds,
        llmClassifierId,
        extendedMetadataKey,
        questionId,
        licensedModuleId,
    };
};

const normalizeCanonicalConfiguration = (
    value: unknown,
    limitsFor: NumericRangeLimitsLookup | undefined,
): GroupByConfiguration | undefined => {
    if (!isRecord(value)) return undefined;

    // The option owns the kind, so each branch checks that the persisted kind and option agree.
    // A configuration that disagrees is malformed data, not something to reinterpret.
    switch (value.kind) {
        case "categorical":
            return normalizeCategoricalConfiguration(
                value.option,
                value.valueIds,
                value.llmClassifierId,
                value.extendedMetadataKey,
                value.questionId,
                value.licensedModuleId,
            );
        case "date":
            return value.option === "Date" && isDateGroupByGrain(value.grain)
                ? {
                      kind: "date",
                      option: "Date",
                      grain: value.grain,
                  }
                : undefined;
        case "numericRange": {
            if (!isNumericRangeGroupByOption(value.option)) return undefined;
            const cutPoints = normalizeCutPoints(
                value.cutPoints,
                limitsFor?.(value.option)?.cutPointCeiling,
            );
            if (!cutPoints) return undefined;

            // Module Score needs its module for the same reason Question Result needs its question:
            // the cut points alone do not say whose score they divide. The backend calls a grouping
            // without one a contradiction and fails the request, so this reader never sends it and
            // the chart renders ungrouped instead.
            const moduleId = toOptionalString(value.moduleId);
            if (value.option === "ModuleScore" && !moduleId) return undefined;

            return {
                kind: "numericRange",
                option: value.option,
                cutPoints,
                moduleId,
            };
        }
        default:
            return undefined;
    }
};

/**
 * Reads the grouping a chart is *configured* with -- canonical `groupBy` first, then the legacy
 * flat fields.
 *
 * Strict on purpose: partially configured grouping (an option with no values, a range with no cut
 * points) normalizes to `undefined`, the same as no grouping at all. A chart cannot be half
 * grouped, so callers get a configuration they can act on or nothing, and never have to re-check
 * completeness.
 *
 * This agrees with `SignalsGroupByResolver` on the backend about *which* configurations are
 * incomplete, but not always on how severely: the backend calls a Model Results grouping with no
 * classifier a contradiction and fails the request, where this returns `undefined` and the chart
 * renders ungrouped. That is deliberate. This reader's job is to decide what to send, and it simply
 * never sends that configuration.
 *
 * `limitsFor` looks an option up in the group-by catalog. A range dimension's ceiling is published
 * rather than structural, so without it that one rule goes unchecked and a chart carrying a boundary
 * past the ceiling reaches the backend, which fails the request instead of rendering ungrouped. It
 * is a lookup rather than a value because the option is not known until the configuration has been
 * parsed. Callers holding the catalog should pass it; the rest keep the structural contract.
 *
 * The per-dimension whole-number rule is not checked here at all. The editor accepts only digits, so
 * it has nothing to catch until a dimension wants fractions, which arrives with the range mode.
 *
 * The configuration drawer needs the opposite; see `readGroupByDraft`.
 */
export const normalizeGroupByConfiguration = (
    source: GroupByConfigurationSource | null | undefined,
    limitsFor?: NumericRangeLimitsLookup,
): GroupByConfiguration | undefined => {
    if (!source) return undefined;

    if (source.groupBy !== undefined) {
        return normalizeCanonicalConfiguration(source.groupBy, limitsFor);
    }

    return normalizeCategoricalConfiguration(
        source.groupByOption,
        source.groupByIds,
        source.groupByLlmClassifierId,
        source.groupByExtendedMetadataKey,
    );
};
